import React from 'react';
import { WorkType, ReviewStatus } from 'src/enums/work';
import { IAttachment, ServerAttachment } from 'src/types/attachments';
import { mapServerAttachmentsToIAttachments } from 'src/utils/attachments';
import AttachmentItem from 'src/shared/AttachmentItem';
import AttachmentPopup from 'src/shared/AttachmentPopup';
import { ActivityHeader } from './styled';
import useAttachmentPopup from './useAttachmentPopup';
import { isAdmin } from 'src/redux/auth/service';
import { ContentProps } from 'src/types/activityUpdate';
import CommunityReviewContent from './CommunityReviewContent';
import TaskSolvedContent from './TaskSolvedContent';
import DefaultContent from './DefaultContent';

interface Props {
    text: string | IAttachment[];
    workType?: WorkType;
    taskTitle?: string;
    taskId?: string;
    grossWorkTime?: string;
    user?: string;
    reviewStatus?: ReviewStatus;
    seniorReviewerName?: string;
}

function renderActivityContent(props: ContentProps): React.ReactNode {
    const { workType, taskTitle } = props;
    if (workType === WorkType.COMMUNITY_REVIEW) {
        return <CommunityReviewContent {...props} />;
    }
    if (taskTitle && isAdmin()) {
        return <TaskSolvedContent {...props} />;
    }
    return <DefaultContent />;
}

const AttachmentActivityHeader: React.FC<Props> = ({
    text,
    taskTitle,
    taskId,
    workType,
    grossWorkTime,
    user,
    reviewStatus,
    seniorReviewerName
}) => {
    let attachmentArray: IAttachment[] = [];
    try {
        if (typeof text === 'string') {
            const parsedData = JSON.parse(text);
            if (
                Array.isArray(parsedData) &&
                parsedData.length > 0 &&
                parsedData[0].id !== undefined
            ) {
                attachmentArray = mapServerAttachmentsToIAttachments(
                    parsedData as ServerAttachment[]
                );
            } else {
                attachmentArray = parsedData;
            }
        } else if (Array.isArray(text)) {
            if (text.length > 0 && text[0].id !== undefined) {
                attachmentArray = mapServerAttachmentsToIAttachments(
                    text as ServerAttachment[]
                );
            } else {
                attachmentArray = text;
            }
        }
    } catch (error) {
        console.error('Failed to parse text:', error);
        attachmentArray = [];
    }
    if (!Array.isArray(attachmentArray)) {
        attachmentArray = [attachmentArray];
    }

    const { popupState, getAttachmentUrl, closePopup, handleNextAttachment } =
        useAttachmentPopup(attachmentArray);

    if (!attachmentArray || attachmentArray.length === 0) {
        return null;
    }

    const contentProps: ContentProps = {
        workType,
        taskTitle,
        taskId,
        grossWorkTime,
        user,
        reviewStatus,
        seniorReviewerName
    };

    return (
        <>
            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={closePopup}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                currentIndex={popupState.currentIndex + 1}
                totalItems={attachmentArray.length}
                onNext={handleNextAttachment}
                fileId={popupState.fileId}
            />
            <ActivityHeader>
                {renderActivityContent(contentProps)}
                {attachmentArray.map((item, index) => (
                    <AttachmentItem
                        key={index}
                        file={item}
                        index={index}
                        getAttachmentUrl={getAttachmentUrl}
                    />
                ))}
            </ActivityHeader>
        </>
    );
};

export default AttachmentActivityHeader;
